import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const SalesAgentCRMDashboard = () => {
  const { user, userProfile, loading } = useAuth();
  const [activeView, setActiveView] = useState('leads');
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);
  const [metrics, setMetrics] = useState({
    totalLeads: 0,
    activeLeads: 0,
    convertedLeads: 0,
    conversionRate: 0
  });
  const [dataLoading, setDataLoading] = useState(true);

  const [newCustomer, setNewCustomer] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    postal_code: '',
    lead_source: 'direct',
    engagement_level: 'medium',
    notes: ''
  });

  useEffect(() => {
    if (user?.id) {
      loadCustomers();
    }
  }, [user]);

  useEffect(() => {
    filterCustomers();
  }, [customers, searchTerm, statusFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user || userProfile?.user_type !== 'sales_agent') {
    return <Navigate to="/provider-login" replace />;
  }

  const loadCustomers = async () => {
    try {
      setDataLoading(true);
      const { data, error } = await supabase
        ?.from('crm_customers')
        ?.select('*')
        ?.eq('provider_id', user?.id)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers(data || []);

      // Calculate metrics
      const total = data?.length || 0;
      const active = data?.filter(c => c?.engagement_level === 'high')?.length || 0;
      const converted = data?.filter(c => c?.lead_source === 'converted')?.length || 0;
      const rate = total > 0 ? Math.round((converted / total) * 100) : 0;

      setMetrics({
        totalLeads: total,
        activeLeads: active,
        convertedLeads: converted,
        conversionRate: rate
      });
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const filterCustomers = () => {
    let filtered = [...customers];

    if (searchTerm) {
      filtered = filtered?.filter(
        (c) =>
          c?.customer_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          c?.customer_email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          c?.customer_phone?.includes(searchTerm)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered?.filter((c) => c?.engagement_level === statusFilter);
    }

    setFilteredCustomers(filtered);
  };

  const handleAddCustomer = async (e) => {
    e?.preventDefault();

    try {
      const { data, error } = await supabase?.from('crm_customers')?.insert([{
        ...newCustomer,
        provider_id: user?.id
      }])?.select();

      if (error) throw error;

      setCustomers([data?.[0], ...customers]);
      setIsAddingCustomer(false);
      setNewCustomer({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_address: '',
        postal_code: '',
        lead_source: 'direct',
        engagement_level: 'medium',
        notes: ''
      });
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Fout bij toevoegen klant. Probeer opnieuw.');
    }
  };

  const getEngagementBadge = (level) => {
    const styles = {
      low: 'bg-muted text-muted-foreground border-border',
      medium: 'bg-warning/10 text-warning border-warning/20',
      high: 'bg-success/10 text-success border-success/20',
    };
    const labels = {
      low: 'Laag',
      medium: 'Gemiddeld',
      high: 'Hoog',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles?.[level]}`}>
        {labels?.[level]}
      </span>
    );
  };

  const views = [
    { id: 'leads', label: 'Mijn Leads', icon: 'Users' },
    { id: 'pipeline', label: 'Klantpipeline', icon: 'TrendingUp' },
    { id: 'performance', label: 'Prestaties', icon: 'BarChart3' },
  ];

  const renderLeadsView = () => (
    <div className="space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Totaal Leads</p>
              <p className="text-3xl font-bold text-foreground mt-2">{metrics?.totalLeads}</p>
            </div>
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={24} className="text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Actieve Leads</p>
              <p className="text-3xl font-bold text-foreground mt-2">{metrics?.activeLeads}</p>
            </div>
            <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={24} className="text-success" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Geconverteerd</p>
              <p className="text-3xl font-bold text-foreground mt-2">{metrics?.convertedLeads}</p>
            </div>
            <div className="w-12 h-12 bg-info/10 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={24} className="text-info" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Conversiepercentage</p>
              <p className="text-3xl font-bold text-foreground mt-2">{metrics?.conversionRate}%</p>
            </div>
            <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Target" size={24} className="text-warning" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Input
              placeholder="Zoek op naam, email of telefoon..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
            />
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e?.target?.value)}
            options={[
              { value: 'all', label: 'Alle statussen' },
              { value: 'low', label: 'Laag engagement' },
              { value: 'medium', label: 'Gemiddeld engagement' },
              { value: 'high', label: 'Hoog engagement' },
            ]}
          />
        </div>
      </div>

      {/* Add Customer Button */}
      <div className="flex justify-end">
        <Button onClick={() => setIsAddingCustomer(true)} iconName="Plus">
          Nieuwe Lead Toevoegen
        </Button>
      </div>

      {/* Customer List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Klant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Locatie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Engagement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Bron
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {dataLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-muted-foreground">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    Laden...
                  </td>
                </tr>
              ) : filteredCustomers?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-muted-foreground">
                    Geen klanten gevonden
                  </td>
                </tr>
              ) : (
                filteredCustomers?.map((customer) => (
                  <tr key={customer?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-foreground">{customer?.customer_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-muted-foreground">{customer?.customer_email}</div>
                      <div className="text-sm text-muted-foreground">{customer?.customer_phone}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-muted-foreground">{customer?.postal_code}</div>
                    </td>
                    <td className="px-6 py-4">
                      {getEngagementBadge(customer?.engagement_level)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-muted-foreground capitalize">{customer?.lead_source}</span>
                    </td>
                    <td className="px-6 py-4">
                      <Button variant="ghost" size="sm" iconName="Eye">
                        Details
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderPipelineView = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Klantpipeline Overzicht</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-border rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-2">Nieuwe Leads</h4>
            <p className="text-3xl font-bold text-primary">{customers?.filter(c => c?.engagement_level === 'low')?.length}</p>
          </div>
          <div className="border border-border rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-2">In Gesprek</h4>
            <p className="text-3xl font-bold text-warning">{customers?.filter(c => c?.engagement_level === 'medium')?.length}</p>
          </div>
          <div className="border border-border rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-2">Klaar voor Afsluiting</h4>
            <p className="text-3xl font-bold text-success">{customers?.filter(c => c?.engagement_level === 'high')?.length}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPerformanceView = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Prestatie Overzicht</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-muted-foreground mb-2">Maandelijkse Doelen</h4>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Nieuwe Leads</span>
                  <span>{metrics?.totalLeads} / 50</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.min((metrics?.totalLeads / 50) * 100, 100)}%` }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Conversies</span>
                  <span>{metrics?.convertedLeads} / 20</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-success h-2 rounded-full" style={{ width: `${Math.min((metrics?.convertedLeads / 20) * 100, 100)}%` }}></div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-muted-foreground mb-2">Commissie Tracking</h4>
            <div className="text-3xl font-bold text-foreground">€ 0</div>
            <p className="text-sm text-muted-foreground mt-1">Deze maand</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeView) {
      case 'leads':
        return renderLeadsView();
      case 'pipeline':
        return renderPipelineView();
      case 'performance':
        return renderPerformanceView();
      default:
        return renderLeadsView();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      <main className="pt-16 lg:pt-0 lg:ml-[72px] transition-all duration-300">
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                    <Icon name="Briefcase" size={24} color="white" />
                  </div>
                  <span>Sales Agent CRM Dashboard</span>
                </h1>
                <p className="text-muted-foreground mt-2">
                  Beheer uw leads, klantpipeline en prestaties
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* View Navigation */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-1 overflow-x-auto">
              {views?.map((view) => (
                <button
                  key={view?.id}
                  onClick={() => setActiveView(view?.id)}
                  className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeView === view?.id
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={view?.icon} size={18} />
                  <span>{view?.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {renderContent()}
        </div>
      </main>

      {/* Add Customer Modal */}
      {isAddingCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-bold text-foreground">Nieuwe Lead Toevoegen</h2>
                <button onClick={() => setIsAddingCustomer(false)} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={24} />
                </button>
              </div>

              <form onSubmit={handleAddCustomer} className="space-y-4">
                <Input
                  label="Naam"
                  value={newCustomer?.customer_name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, customer_name: e?.target?.value })}
                  required
                />
                <Input
                  label="Email"
                  type="email"
                  value={newCustomer?.customer_email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, customer_email: e?.target?.value })}
                  required
                />
                <Input
                  label="Telefoon"
                  value={newCustomer?.customer_phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, customer_phone: e?.target?.value })}
                  required
                />
                <Input
                  label="Adres"
                  value={newCustomer?.customer_address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, customer_address: e?.target?.value })}
                />
                <Input
                  label="Postcode"
                  value={newCustomer?.postal_code}
                  onChange={(e) => setNewCustomer({ ...newCustomer, postal_code: e?.target?.value })}
                />
                <Select
                  label="Lead Bron"
                  value={newCustomer?.lead_source}
                  onChange={(e) => setNewCustomer({ ...newCustomer, lead_source: e?.target?.value })}
                  options={[
                    { value: 'direct', label: 'Direct Contact' },
                    { value: 'referral', label: 'Verwijzing' },
                    { value: 'website', label: 'Website' },
                    { value: 'event', label: 'Evenement' },
                  ]}
                />
                <Select
                  label="Engagement Level"
                  value={newCustomer?.engagement_level}
                  onChange={(e) => setNewCustomer({ ...newCustomer, engagement_level: e?.target?.value })}
                  options={[
                    { value: 'low', label: 'Laag' },
                    { value: 'medium', label: 'Gemiddeld' },
                    { value: 'high', label: 'Hoog' },
                  ]}
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Notities</label>
                  <textarea
                    value={newCustomer?.notes}
                    onChange={(e) => setNewCustomer({ ...newCustomer, notes: e?.target?.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsAddingCustomer(false)}>
                    Annuleren
                  </Button>
                  <Button type="submit">
                    Lead Toevoegen
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesAgentCRMDashboard;